// Define custom-slider-component
if (!customElements.get('swiper-slider-component')) {
  customElements.define(
    'swiper-slider-component',
    class CustomSliderComponent extends HTMLElement {
      constructor() {
        super();
        this.swiperWrapper = this.querySelector('.swiper-wrapper');
        this.swiperInstance = null;
      }

      connectedCallback() {
        if(this.swiperWrapper){
          this.initBasedOnDevice();
          window.addEventListener('resize', this.initBasedOnDevice.bind(this));
        }
      }

      initBasedOnDevice() {
        const pcSlider = this.getAttribute('pc-slider') === 'true';
        const mobileSlider = this.getAttribute('mobile-slider') === 'true';
        const screenWidth = window.innerWidth;

        const shouldInit =
          (screenWidth <= 768 && mobileSlider) ||
          (screenWidth > 769 && pcSlider);

        if (shouldInit && !this.swiperInstance) {
          this.initializeSwiper();
        } else if (!shouldInit && this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;

          // Clear navigation buttons
          const bannerNav = this.querySelector('.banner-navigation');
          if (bannerNav) bannerNav.innerHTML = '';
        }
      }

      sliderNavigation() {
        const navElements = this.querySelectorAll('.navigation-button-wrapper');
        const bannerNav = this.querySelector('.banner-navigation');
        if (!bannerNav) return;

        bannerNav.innerHTML = ''; // clear old nav buttons

        navElements.forEach((el, index) => {
          const clone = el.cloneNode(true);

          // Click event to change slide
          clone.addEventListener('click', () => {
            if (this.swiperInstance) {
              this.swiperInstance.slideTo(index);
            }
          });

          bannerNav.appendChild(clone);
        });
      }

      animateSlideContent(activeSlide, prevSlide, direction) {
        if (!activeSlide) return;

        // Animate active slide in
        const activeItems = activeSlide.querySelectorAll('h1, h2, h3, p');
        activeItems.forEach((el, i) => {
          el.classList.remove('fadeInLeft', 'fadeInRight', 'fadeOutLeft', 'fadeOutRight', 'animated');
          void el.offsetWidth; // restart animation
          el.style.animationDelay = `${i * 0.2}s`;
          // Incoming slide animation
          if (direction === 'next') {
            el.classList.add('fadeInRight', 'animated');
          } else {
            el.classList.add('fadeInLeft', 'animated');
          }
        });

        // Animate previous slide out
        if (prevSlide) {
          const prevItems = prevSlide.querySelectorAll('h1, h2, h3, p');
          prevItems.forEach((el) => {
            el.classList.remove('fadeInLeft', 'fadeInRight', 'fadeOutLeft', 'fadeOutRight', 'animated');
            void el.offsetWidth;
            // Outgoing slide animation
            if (direction === 'next') {
              el.classList.add('fadeOutLeft', 'animated');
            } else {
              el.classList.add('fadeOutRight', 'animated'); // <-- previous slide goes left
            }
          });
        }
      }

      initializeSwiper() {
        if (!window.Swiper) {
          const swiperScript = document.createElement('script');
          swiperScript.src =
            'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
          swiperScript.onload = () => this.initSwiperInstance();
          document.head.appendChild(swiperScript);
        } else {
          this.initSwiperInstance();
        }
      }

      initSwiperInstance() {
        const pcItems =
          parseFloat(this.swiperWrapper.getAttribute('data-carousel-item-pc')) || 3;
        const mobileItems =
          parseFloat(this.swiperWrapper.getAttribute('data-carousel-item-mobile')) || 1.5;
        const tabletItems =
          parseFloat(this.swiperWrapper.getAttribute('data-carousel-item-tablet')) || 1.5;
        const centerSlide = this.getAttribute('center-slide') === 'true';
        const loopSlide = this.getAttribute('data-carousel-item-loop') === 'true';

        const isMobile = window.innerWidth < 1024;
        const pcSlider = this.getAttribute('pc-slider') === 'true';
        const mobileSlider = this.getAttribute('mobile-slider') === 'true';
        const shouldShowControls =
          (isMobile && mobileSlider) || (!isMobile && pcSlider);

        this.swiperInstance = new Swiper(this.querySelector('.swiper'), {
          loop: loopSlide,
          spaceBetween: 32,
          centeredSlides: isMobile,
          pagination: shouldShowControls
            ? { el: this.querySelector('.swiper-pagination') }
            : false,
          navigation: shouldShowControls
            ? {
                nextEl: this.querySelector('.swiper-button-next'),
                prevEl: this.querySelector('.swiper-button-prev'),
              }
            : {},
          scrollbar: shouldShowControls
            ? { el: this.querySelector('.swiper-scrollbar'), hide: true }
            : false,
          breakpoints: {
            300: {
              slidesPerView: mobileItems,
              centeredSlides: centerSlide,
              spaceBetween: 16,
            },
            580: { slidesPerView: tabletItems, centeredSlides: centerSlide, spaceBetween: 16 },
            780: { slidesPerView: tabletItems, centeredSlides: centerSlide },
            1024: { slidesPerView: pcItems, centeredSlides: false },
          },
          on: {
            afterInit: (swiper) => {
              if (isMobile && swiper.activeIndex === 0) {
                swiper.wrapperEl.style.transform = 'translate3d(0px, 0, 0)';
              }

              // IntersectionObserver fade-in
              const observer = new IntersectionObserver((entries, obs) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    this.querySelectorAll('.swiper-slide').forEach((el, index) => {
                      setTimeout(() => {
                        el.style.opacity = '1';
                        el.style.transition = 'opacity 0.3s ease';
                      }, 200 * index);
                    });
                    obs.unobserve(entry.target);
                  }
                });
              }, { threshold: 0.3 });

              observer.observe(this);

              // Initialize navigation buttons after Swiper is ready
              this.sliderNavigation();

              // Animate first slide
              const firstSlide = swiper.slides[swiper.activeIndex];
              this.animateSlideContent(firstSlide, null, 'next');
            },

            slideChange: () => {
              // Sync active nav button
              const activeIndex = this.swiperInstance.realIndex;
              const navButtons = this.querySelectorAll('.banner-navigation .navigation-button-wrapper');
              navButtons.forEach((btn, i) => {
                btn.classList.toggle('active', i === activeIndex);
              });
            },

            slideChangeTransitionStart: (swiper) => {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const prevSlide = swiper.slides[swiper.previousIndex];
              const direction = swiper.activeIndex > swiper.previousIndex ? 'next' : 'prev';

              this.animateSlideContent(activeSlide, prevSlide, direction);
            },
          },
        });

        // Hide controls if pcSlider is false
        if (!isMobile && !pcSlider) {
          this.querySelectorAll('.swiper-button-prev, .swiper-button-next, .swiper-pagination')
            .forEach(el => el.style.display = 'none');
        }
      }
    }
  );
}
